/**
 * Session-state DOM overlay rendered above the Phaser canvas.
 *
 * Three states:
 *   active     → hidden.
 *   recovering → translucent dim with a small spinner + "Reconnecting…".
 *   banned     → modal blocker with the banned reason and a Refresh button.
 *
 * Implementation uses plain DOM rather than Phaser-side rendering because
 * (a) it survives scene transitions without per-scene wiring and
 * (b) it stays interactive (button) without dragging in Phaser DOMElement.
 */

export type OverlayState = 'active' | 'recovering' | 'offline' | 'banned';

interface BannedDetails {
  reason?: unknown;
}

const STYLE = `
  .sb-overlay {
    position: fixed;
    inset: 0;
    display: none;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 16px;
    font-family: system-ui, -apple-system, sans-serif;
    color: #eeeeee;
    pointer-events: none;
    z-index: 9999;
  }
  .sb-overlay[data-state="recovering"],
  .sb-overlay[data-state="offline"] {
    display: flex;
    background: rgba(11, 11, 20, 0.55);
  }
  .sb-overlay[data-state="banned"] {
    display: flex;
    background: rgba(11, 11, 20, 0.92);
    pointer-events: auto;
  }
  .sb-overlay__spinner {
    width: 28px;
    height: 28px;
    border: 3px solid rgba(238, 238, 238, 0.25);
    border-top-color: #4fff7a;
    border-radius: 50%;
    animation: sb-spin 0.9s linear infinite;
  }
  .sb-overlay__title { font-size: 18px; }
  .sb-overlay__sub { font-size: 13px; color: #9aa0b4; max-width: 480px; text-align: center; }
  .sb-overlay__btn {
    pointer-events: auto;
    padding: 8px 18px;
    background: #4fff7a;
    color: #0b0b14;
    border: 0;
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
  }
  @keyframes sb-spin { to { transform: rotate(360deg); } }
`;

interface OverlayHandle {
  setState(state: OverlayState, details?: BannedDetails): void;
}

export function mountSessionOverlay(): OverlayHandle {
  const style = document.createElement('style');
  style.textContent = STYLE;
  document.head.appendChild(style);

  const root = document.createElement('div');
  root.className = 'sb-overlay';
  root.dataset.state = 'active';
  document.body.appendChild(root);

  const spinner = document.createElement('div');
  spinner.className = 'sb-overlay__spinner';
  const title = document.createElement('div');
  title.className = 'sb-overlay__title';
  const sub = document.createElement('div');
  sub.className = 'sb-overlay__sub';
  const btn = document.createElement('button');
  btn.className = 'sb-overlay__btn';
  btn.textContent = 'Refresh page';
  btn.addEventListener('click', () => location.reload());

  function render(state: OverlayState, details?: BannedDetails): void {
    root.replaceChildren();
    root.dataset.state = state;

    if (state === 'active') return;

    if (state === 'recovering' || state === 'offline') {
      title.textContent = state === 'offline' ? 'Disconnected' : 'Reconnecting…';
      sub.textContent =
        state === 'offline'
          ? 'Lost connection to the server. SDK will retry automatically.'
          : 'Restoring your session.';
      root.append(spinner, title, sub);
      return;
    }

    if (state === 'banned') {
      title.textContent = 'You have been disconnected';
      const reason =
        details && typeof details.reason === 'string' && details.reason
          ? details.reason
          : 'No reason provided.';
      sub.textContent = `Reason: ${reason}`;
      root.append(title, sub, btn);
    }
  }

  return {
    setState: render,
  };
}
